import type { JsonObject, ToolDef } from "@fabric-harness/sdk";
import type {
  CreatePipelineRequest,
  PipelineLibrary,
  PipelinesClient,
} from "@databricks/sdk-pipelines/v2";
import { runStatement, type DatabricksStatementClient } from "./sql-exec.js";

export type DatabricksPipelinesAuthoringClient = Pick<PipelinesClient, "create" | "edit" | "delete">;

export interface DatabricksPipelineLibrary {
  notebook?: { path: string };
  file?: { path: string };
  jar?: string;
  whl?: string;
}
export interface DatabricksPipelineSpec {
  name: string;
  libraries: DatabricksPipelineLibrary[];
  catalog?: string;
  target?: string;
  continuous?: boolean;
  serverless?: boolean;
  development?: boolean;
  configuration?: Record<string, string>;
  clusters?: JsonObject[];
}

export class DatabricksLakeflowAuthoring {
  constructor(
    private readonly client: DatabricksPipelinesAuthoringClient,
    private readonly statements?: DatabricksStatementClient,
  ) {}
  async createPipeline(spec: DatabricksPipelineSpec): Promise<{ pipelineId: string }> {
    const response = await this.client.create(serialize(spec));
    if (!response.pipelineId)
      throw new Error("Databricks Lakeflow create response did not contain pipeline_id.");
    return { pipelineId: response.pipelineId };
  }
  async updatePipeline(pipelineId: string, spec: DatabricksPipelineSpec): Promise<void> {
    await this.client.edit({ pipelineId, id: pipelineId, ...serialize(spec) });
  }
  async deletePipeline(pipelineId: string): Promise<void> {
    await this.client.delete({ pipelineId });
  }
  async pipelineEvents(pipelineId: string, warehouseId: string, limit = 100): Promise<unknown> {
    const escaped = pipelineId.replace(/'/g, "''");
    if (!this.statements)
      throw new Error("Databricks Lakeflow events require a Statement Execution client.");
    return runStatement(
      this.statements,
      warehouseId,
      `SELECT * FROM event_log('${escaped}') ORDER BY timestamp DESC LIMIT ${Math.max(1, Math.min(1000, Math.floor(limit)))}`,
      [],
      { intervalMs: 2_000, timeoutMs: 120_000 },
    );
  }
}

export function databricksLakeflowAuthoringTools(
  client: DatabricksPipelinesAuthoringClient,
  options: { warehouseId?: string; statements?: DatabricksStatementClient } = {},
): ToolDef[] {
  const api = new DatabricksLakeflowAuthoring(client, options.statements);
  const spec = specSchema();
  const specProperties = spec.properties as JsonObject;
  const resources = [
    { kind: "pipeline", path: "/name" },
    { kind: "catalog", path: "/catalog", catalogQualified: true, optional: true },
    { kind: "notebook", path: "/libraries/*/notebook/path", optional: true },
  ];
  const tools: ToolDef[] = [
    tool("databricks_create_pipeline", "write", spec, resources, (input) =>
      api.createPipeline(input as unknown as DatabricksPipelineSpec),
    ),
    tool(
      "databricks_update_pipeline",
      "write",
      {
        type: "object",
        additionalProperties: false,
        properties: { pipelineId: { type: "string" }, ...specProperties },
        required: ["pipelineId", "name", "libraries"],
      },
      [{ kind: "pipeline", path: "/pipelineId" }, ...resources.slice(1)],
      (input) =>
        api.updatePipeline(input.pipelineId as string, input as unknown as DatabricksPipelineSpec),
    ),
    tool(
      "databricks_delete_pipeline",
      "write",
      idSchema(),
      [{ kind: "pipeline", path: "/pipelineId" }],
      (input) => api.deletePipeline(input.pipelineId as string),
    ),
  ];
  const warehouseId = options.warehouseId;
  if (warehouseId)
    tools.push(
      tool(
        "databricks_pipeline_events",
        "read",
        {
          type: "object",
          additionalProperties: false,
          properties: { pipelineId: { type: "string" }, limit: { type: "number" } },
          required: ["pipelineId"],
        },
        [{ kind: "pipeline", path: "/pipelineId" }],
        (input) =>
          api.pipelineEvents(
            input.pipelineId as string,
            warehouseId,
            input.limit as number | undefined,
          ),
      ),
    );
  return tools;
}

function tool(
  name: string,
  effect: "read" | "write",
  inputSchema: JsonObject,
  resources: JsonObject[],
  execute: (input: JsonObject) => unknown,
): ToolDef {
  return {
    name,
    description: name.replaceAll("_", " "),
    inputSchema,
    metadata: { effect, provider: "databricks", service: "lakeflow", governance: { resources } },
    execute,
  };
}
function idSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: { pipelineId: { type: "string" } },
    required: ["pipelineId"],
  };
}
function specSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      name: { type: "string" },
      libraries: {
        type: "array",
        items: {
          type: "object",
          properties: {
            notebook: { type: "object", properties: { path: { type: "string" } } },
            file: { type: "object", properties: { path: { type: "string" } } },
            jar: { type: "string" },
            whl: { type: "string" },
          },
        },
      },
      catalog: { type: "string" },
      target: { type: "string" },
      continuous: { type: "boolean" },
      serverless: { type: "boolean" },
      development: { type: "boolean" },
      configuration: { type: "object", additionalProperties: { type: "string" } },
      clusters: { type: "array", items: { type: "object", additionalProperties: true } },
    },
    required: ["name", "libraries"],
  };
}
function serialize(spec: DatabricksPipelineSpec): CreatePipelineRequest {
  return {
    name: spec.name,
    libraries: spec.libraries.map(serializeLibrary),
    ...(spec.catalog ? { catalog: spec.catalog } : {}),
    ...(spec.target ? { target: spec.target } : {}),
    ...(spec.continuous !== undefined ? { continuous: spec.continuous } : {}),
    ...(spec.serverless !== undefined ? { serverless: spec.serverless } : {}),
    ...(spec.development !== undefined ? { development: spec.development } : {}),
    ...(spec.configuration ? { configuration: spec.configuration } : {}),
    ...(spec.clusters ? { clusters: spec.clusters as CreatePipelineRequest["clusters"] } : {}),
  };
}

function serializeLibrary(library: DatabricksPipelineLibrary): PipelineLibrary {
  if (library.notebook)
    return { lib: { $case: "notebook", notebook: { path: library.notebook.path } } };
  if (library.file) return { lib: { $case: "file", file: { path: library.file.path } } };
  if (library.jar) return { lib: { $case: "jar", jar: library.jar } };
  if (library.whl) return { lib: { $case: "whl", whl: library.whl } };
  throw new Error("Databricks Lakeflow library requires notebook, file, jar, or whl.");
}
